import { useState, useEffect, useContext, useCallback } from "react";
import { Button } from "@stackje/shared-components/ui/button";
import { ChevronLeft, Copy, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Label } from "@stackje/shared-components/ui/label";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { TopBar } from "./shared/TopBar";
import { ProBadge } from "./shared/ProBadge";
import { ApiClient } from "@/lib/apiClient";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import type { Site } from "@/types/site.types";
import type { CustomDomainValidationRecords } from "@stackje/common/config";
import { AuthContext } from "@/context/AuthContext";
import { useSubscriptionQuery } from "@/hooks/useSubscriptionQuery";
import { getDomain } from "@/utils/route-utils";
import { useInvalidateSiteCache } from "@/hooks/useSiteQuery";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";

interface CustomDomainStatus {
  domain: string;
  status: string;
  sslStatus: string;
  verificationErrors?: string[];
  validationRecords: CustomDomainValidationRecords | null;
}

export function CustomDomainPage({ site }: { site: Site }) {
  const { toast } = useToast();
  const authContext = useContext(AuthContext);
  const { data: subscription } = useSubscriptionQuery(authContext?.user?.id);
  const isPro = subscription?.plan_type === 'pro';
  const invalidateSiteCache = useInvalidateSiteCache();

  const [domain, setDomain] = useState(site.customDomain || "");
  const [domainError, setDomainError] = useState("");
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [status, setStatus] = useState<CustomDomainStatus | null>(null);
  const [hasConfiguredDomain, setHasConfiguredDomain] = useState(!!site.customDomain);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const handleBackClick = () => {
    history.back();
  };

  const validateDomain = (domainValue: string): string => {
    if (!domainValue.trim()) {
      return "Domain is required";
    }

    const trimmedDomain = domainValue.trim();

    // Check for uppercase letters
    if (trimmedDomain !== trimmedDomain.toLowerCase()) {
      return "Domain must be lowercase";
    }

    // Basic domain validation regex
    const domainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/;

    if (!domainRegex.test(trimmedDomain)) {
      return "Please enter a valid domain (e.g., example.com)";
    }

    // Check for common mistakes
    if (trimmedDomain.includes("http://") || trimmedDomain.includes("https://")) {
      return "Enter domain without http:// or https://";
    }

    if (trimmedDomain.includes("/")) {
      return "Enter domain without paths (e.g., example.com not example.com/page)";
    }

    return "";
  };

  const handleDomainChange = (value: string) => {
    // Convert to lowercase automatically
    const lowercaseValue = value.toLowerCase();
    setDomain(lowercaseValue);

    // Clear error when user starts typing
    if (domainError) {
      setDomainError("");
    }
  };

  const checkDomainStatus = useCallback(async () => {
    if (!hasConfiguredDomain) return;

    setIsCheckingStatus(true);
    try {
      const result = await ApiClient.getCustomDomainStatus(site.id);
      if (result.error) {
        captureErrorAndToast(result.error, toast, {
          description: result.error.message
        });
      } else if (result.data) {
        setStatus(result.data);
      }
    } catch (error) {
      captureErrorAndToast(error, toast, {
        description: "Failed to check domain status"
      });
    } finally {
      setIsCheckingStatus(false);
    }
  }, [hasConfiguredDomain, site.id, toast]);

  const configureDomain = async () => {
    const trimmedDomain = domain.trim();
    const validationError = validateDomain(trimmedDomain);

    if (validationError) {
      setDomainError(validationError);
      return;
    }

    setIsConfiguring(true);
    try {
      const result = await ApiClient.configureCustomDomain(site.id, { domain: trimmedDomain });
      if (result.error) {
        captureErrorAndToast(result.error, toast, {
          description: result.error.message
        });
      } else if (result.data) {
        setHasConfiguredDomain(true);
        setStatus({
          domain: result.data.domain,
          status: "pending_validation",
          sslStatus: "pending",
          validationRecords: result.data.validationRecords,
        });
        toast({
          description: "Custom domain configured successfully",
        });

        // Invalidate site cache to refresh data
        invalidateSiteCache(site.userId);
      }
    } catch (error) {
      captureErrorAndToast(error, toast, {
        description: "Failed to configure custom domain"
      });
    } finally {
      setIsConfiguring(false);
    }
  };

  const removeDomain = async () => {
    setIsRemoving(true);
    setShowRemoveDialog(false);
    try {
      const result = await ApiClient.removeCustomDomain(site.id);
      if (result.error) {
        captureErrorAndToast(result.error, toast, {
          description: result.error.message
        });
      } else {
        setHasConfiguredDomain(false);
        setStatus(null);
        setDomain("");
        toast({
          description: "Custom domain removed successfully",
        });

        // Invalidate site cache to refresh data
        invalidateSiteCache(site.userId);
      }
    } catch (error) {
      captureErrorAndToast(error, toast, {
        description: "Failed to remove custom domain"
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        description: "Copied to clipboard",
      });
    } catch {
      toast({
        variant: "destructive",
        description: "Failed to copy to clipboard",
      });
    }
  };

  const getCNAMERecordName = (domain: string): string => {
    if (!domain) return "@";

    // Split domain into parts
    const parts = domain.toLowerCase().split('.');

    // If it's a root domain (e.g., example.com) or www subdomain
    if (parts.length === 2 || (parts.length === 3 && parts[0] === 'www')) {
      return "@ (for root domain) or www (for www subdomain)";
    }

    // For subdomains (e.g., blog.example.com), use the subdomain part
    return parts[0];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending_validation":
      case "pending_deployment":
      case "pending_ssl_validation":
      case "pending_ssl_deployment":
        return <Loader2 className="h-4 w-4 text-orange-500 animate-spin" />;
      case "ssl_error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Active";
      case "pending_validation":
        return "Verifying DNS Records";
      case "pending_deployment":
        return "Deploying Configuration";
      case "pending_ssl_validation":
        return "Validating SSL Certificate";
      case "pending_ssl_deployment":
        return "Deploying SSL Certificate";
      case "ssl_error":
        return "SSL Certificate Error";
      default:
        return "Needs Attention";
    }
  };

  // Load initial status if domain is already configured
  useEffect(() => {
    if (hasConfiguredDomain) {
      checkDomainStatus();
    }
  }, [hasConfiguredDomain, checkDomainStatus]);

  return (
    <div className="pt-6 pb-6 lg:pt-10 lg:pb-10">
      <TopBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1>Custom Domain</h1>
        <div className="w-8" />
      </TopBar>

      <div className="space-y-6 px-8 py-16 max-w-2xl mx-auto w-full">
        {/* Current Domain */}
        <div className="space-y-2">
          <Label>Your Stack.je URL</Label>
          <div className="p-3 bg-muted rounded-md">
            <span className="text-sm font-medium">https://{site.subdomain}.{getDomain()}</span>
          </div>
        </div>

        {/* Custom Domain Configuration */}
        <div className="space-y-4 border rounded-lg p-6">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Custom Domain Configuration</h2>
            <ProBadge isPro={isPro} />
          </div>

          {!hasConfiguredDomain ? (
            /* Domain Input Form */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="domain">Domain</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <ClearableInput
                      id="domain"
                      value={domain}
                      onChange={handleDomainChange}
                      placeholder="example.com"
                      disabled={!isPro || isConfiguring}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {domainError && (
                      <p className="text-sm text-red-600 mt-1">{domainError}</p>
                    )}
                  </div>
                  <Button
                    onClick={configureDomain}
                    disabled={!isPro || isConfiguring || !domain.trim()}
                    className="mt-2 mb-2"
                  >
                    {isConfiguring ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Configuring...
                      </>
                    ) : (
                      "Configure"
                    )}
                  </Button>
                </div>
                <div className="text-sm text-black mt-1">
                  You must be able to change the DNS records for the domain.
                </div>
              </div>
            </div>
          ) : (
            /* Domain Status and Instructions */
            <div className="space-y-4">
              <div className="flex flex-col w-full">
                <p>Domain: <span className="font-medium">{status?.domain || site.customDomain}</span></p>
                <div className="flex items-center gap-2 flex-row justify-between w-full">
                  {status && (
                    <div className="flex flex-row items-center gap-2">
                      {getStatusIcon(status.status)}
                      <span className="text-sm text-muted-foreground">
                        {getStatusText(status.status)}
                      </span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkDomainStatus}
                    disabled={isCheckingStatus || isRemoving}
                  >
                    {isCheckingStatus ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "Check Status"
                    )}
                  </Button>
                </div>
              </div>

              {/* DNS Configuration Instructions */}
              {status?.validationRecords && status.status !== "active" && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-orange-900">
                        {status.status === "pending_validation" || status.status === "pending_ssl_validation" || status.status === "pending_ssl_deployment"
                          ? "DNS Configuration"
                          : "DNS Configuration Required"}
                      </h3>
                      <p className="text-sm text-orange-700 mt-1">
                        {status.status === "pending_validation"
                          ? "We're verifying your DNS records. This usually takes a few minutes but can take up to 24 hours for DNS changes to propagate globally."
                          : status.status === "pending_ssl_validation"
                          ? "DNS verification complete! We're now validating your SSL certificate. This process usually takes a few minutes."
                          : status.status === "pending_ssl_deployment"
                          ? "SSL certificate validated! We're deploying your secure configuration. This should complete shortly."
                          : "Add these records to your DNS provider:"
                        }
                      </p>
                    </div>
                  </div>

                  {/* Show helpful tip during different phases */}
                  {status.status === "pending_validation" && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 mb-4">
                      <strong>Tip:</strong> If you've already added the DNS records, please allow time for DNS propagation.
                      You can click "Check Status" periodically to see if verification is complete.
                    </div>
                  )}
                  {status.status === "pending_ssl_validation" && status.validationRecords?.ssl && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 mb-4">
                      <strong>Action Required:</strong> Your DNS records are verified! Please add the SSL validation TXT record below to complete SSL certificate issuance. Please allow some time for the SSL certificate to be issued once you add the record.
                    </div>
                  )}
                  {status.status === "pending_ssl_deployment" && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 mb-4">
                      <strong>Almost Done!</strong> SSL certificate validated and deploying. There may be additional steps to complete the setup. Please check back in a few minutes.
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* CNAME Record */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">1. CNAME Record</h4>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Name:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs">
                            {getCNAMERecordName(status?.domain || domain)}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(getCNAMERecordName(status?.domain || domain))}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Value:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs">
                            {site.subdomain}.{getDomain()}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`${site.subdomain}.${getDomain()}`)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {/* Show helpful note for root domains */}
                      {(status?.domain || domain) && (status?.domain || domain).split('.').length === 2 && (
                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <strong>Note:</strong> Use "@" for the root domain ({status?.domain || domain}) or "www" for the www subdomain (www.{status?.domain || domain}).
                        </div>
                      )}
                    </div>

                    {/* TXT Record */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">2. TXT Record (Verification)</h4>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Name:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs">
                            {status.validationRecords.txt.name}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(status.validationRecords!.txt.name)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Value:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs break-all">
                            {status.validationRecords.txt.value}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(status.validationRecords!.txt.value)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* SSL Validation TXT Record - show during SSL validation phases */}
                    {status.validationRecords?.ssl && (status.status === "pending_ssl_validation" || status.status === "pending_ssl_deployment") && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">3. SSL Validation TXT Record</h4>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 mb-2">
                          <strong>SSL Certificate Validation:</strong> Add this additional TXT record to complete SSL certificate issuance.
                        </div>
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Name:</span>
                            <code className="bg-white px-2 py-1 rounded text-xs">
                              {status.validationRecords.ssl.txt_name}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(status.validationRecords?.ssl?.txt_name || '')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Value:</span>
                            <code className="bg-white px-2 py-1 rounded text-xs break-all">
                              {status.validationRecords.ssl.txt_value}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(status.validationRecords?.ssl?.txt_value || '')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {status?.verificationErrors && status.verificationErrors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-red-900">Configuration Errors</h3>
                      <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                        {status.verificationErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {status?.status === "ssl_error" && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-red-900">SSL Certificate Error</h3>
                      <p className="text-sm text-red-700 mt-1">
                        There was an issue with your SSL certificate setup. This is usually temporary.
                        Please try clicking "Check Status" to retry, or contact support if the issue persists.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {status?.status === "active" && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <h3 className="font-medium text-green-900">Domain Active</h3>
                      <p className="text-sm text-green-700">
                        Your custom domain is successfully configured and active!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={isRemoving || isCheckingStatus}
                  onClick={() => setShowRemoveDialog(true)}
                >
                  {isRemoving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    "Remove Domain"
                  )}
                </Button>

                <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Remove Custom Domain</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to remove the custom domain "{status?.domain || site.customDomain}"?
                        This will immediately disconnect your custom domain and visitors will no longer be able to access your site through this domain.
                        <br />
                        <br />
                        This action cannot be undone, but you can reconfigure the domain later if needed.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowRemoveDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={removeDomain}
                        disabled={isRemoving}
                      >
                        {isRemoving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Removing...
                          </>
                        ) : (
                          "Remove Domain"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}